import axios from 'axios';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_PATH = '/api/v1';

// Main API instance for /api/v1 endpoints
const api = axios.create({
  baseURL: `${API_BASE_URL}${API_PATH}`,
  withCredentials: true,
  timeout: 15000, // Set a reasonable timeout to prevent hanging requests
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Special instance for file uploads that doesn't set Content-Type
const fileUploadApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PATH}`,
  withCredentials: true,
  timeout: 30000, // Longer timeout for file uploads
});

// Public API instance for endpoints that don't require authentication
const publicApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PATH}`,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Auth API instance for /auth endpoints
const authApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PATH}`,
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Cookies will be sent automatically with withCredentials:true
api.interceptors.request.use(
  (config) => {
    // No need to manually add token - cookies are sent automatically
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Setup response interceptor for authentication errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check if the error is due to an expired token
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.log('API: Authentication error detected, triggering token refresh');
      
      // Try to refresh the token
      try {
        // Don't use authApi here to avoid circular dependencies
        const response = await axios.post('/api/v1/auth/refresh', {}, {
          timeout: 5000,
          withCredentials: true, // Important for sending/receiving cookies
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
          }
        });
        
        if (response.data && response.data.status === "success") {
          console.log('API: Token refreshed successfully, retrying original request');
          
          // No need to store or set tokens - cookies are handled by the browser
          
          // Retry the original request - cookies will be sent automatically
          return axios(error.config);
        }
      } catch (refreshError) {
        console.error('API: Token refresh failed, logging out locally.', refreshError);
        
        // If token refresh fails, clear user data - tokens are HttpOnly cookies
        localStorage.removeItem('user');
        
        // REMOVE Redirection - Let the calling component handle the error/state change
        // const currentPath = window.location.pathname;
        // if (currentPath !== '/login' && currentPath !== '/') {
        //   sessionStorage.setItem('auth_redirect_path', currentPath);
        // }
        // window.location.href = '/';
        
        // Reject the promise so the original caller knows the refresh failed
        return Promise.reject(refreshError);
      }
    }
    
    // For other errors, just reject the promise
    return Promise.reject(error);
  }
);

// No auth interceptor needed for publicApi
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Public API Error:', error);
    return Promise.reject(error);
  }
);

authApi.interceptors.request.use(
  (config) => {
    // No need to add token - cookies will be sent automatically
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Auth API Error:', error);
    return Promise.reject(error);
  }
);

// Cookies will be sent automatically
fileUploadApi.interceptors.request.use(
  (config) => {
    // No need to add token - cookies will be sent automatically
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { api, authApi, publicApi, fileUploadApi }; 